import React, { FC, ReactNode } from "react";
import { defineMessages, useIntl } from "react-intl";
import { TestType } from "@app/provider/testData/types";
import Svg from "@natera/material/lib/svg";
import DocumentsMagnifyingGlass from "@assets/svg/documents-magnifying-glass-filled.svg";

import "../understandingYourResults.scss";
import { BusinessUnits } from "@app/provider/drawRequest";
import useTestResultsTexts from "@app/hooks/useTestResultsTexts";

const messages = defineMessages({
  understandingYourResultsOncologySignateraHeadline: {
    id: "understandingYourResultsOncologySignateraHeadline",
    defaultMessage: "Signatera",
  },
  understandingYourResultsOncologyEmpowerHeadline: {
    id: "understandingYourResultsOncologyEmpowerHeadline",
    defaultMessage: "Empower",
  },
  understandingYourResultsOncologyAlteraHeadline: {
    id: "understandingYourResultsOncologyAlteraHeadline",
    defaultMessage: "Altera",
  },
  understandingYourResultsReadysgNIPTHeadline: {
    id: "understandingYourResultsReadysgNIPTHeadline",
    defaultMessage: "Fetal Focus",
  },
  understandingYourResultsReadyLatitudeHeadline: {
    id: "understandingYourResultsReadyLatitudeHeadline",
    defaultMessage: "Latitude",
  },
  understandingYourResultsOncologySignateraFirstParagraph: {
    id: "understandingYourResultsOncologySignateraFirstParagraph",
    defaultMessage:
      "The Signatera test uses genetic signatures from your tumor to create a personalized test that can look for that tumor’s DNA in your blood. The results can help to determine if there are signs of cancer still in the body after surgery or cancer therapy, to determine if the treatment is working, or to find out if your cancer may be coming back. The following results are possible:",
  },
  understandingYourResultsOncologyEmpowerFirstParagraph: {
    id: "understandingYourResultsOncologyEmpowerFirstParagraph",
    defaultMessage:
      "The Empower Hereditary Cancer Test screens for variants, or mutations, in genes associated with having an increased risk for hereditary cancers. The following results are possible:",
  },
  understandingYourResultsOncologyAlteraFirstParagraph: {
    id: "understandingYourResultsOncologyAlteraFirstParagraph",
    defaultMessage:
      "The Altera tumor genomic profile is a test that looks for specific genetic changes in your tumor. This information, along with your personal cancer history, can be used by healthcare providers to help guide treatment decisions.{br}Your test results will include the following sections below. These results should be reviewed with your healthcare provider.",
  },
  understandingYourResultsOncologyLatitudeFirstParagraph: {
    id: "understandingYourResultsOncologyLatitudeFirstParagraph",
    defaultMessage:
      "Our bodies have a specific mechanism, called methylation, that regulates how the body turns genes on and off. Cancer methylation signatures are unique and specific to each cancer type. Latitude is a blood test that looks for these signatures in your cancer type from tumor DNA that may be present in your blood. It can provide key insights to inform your personalized treatment plan without needing a tissue sample. The following results are possible:",
  },
  understandingYourResultsOncologyReflexToLatitudeFirstParagraph: {
    id: "understandingYourResultsOncologyReflexToLatitudeFirstParagraph",
    defaultMessage:
      "Our bodies have a specific mechanism, called methylation, that regulates how the body turns genes on and off. Cancer methylation signatures are unique and specific to each cancer type. Latitude is a blood test that looks for these signatures in your cancer type from tumor DNA that may be present in your blood.{br}{br}Signatera uses genetic signatures from your tumor to create a personalized test that can look for that tumor’s DNA in your blood. Signatera requires tumor tissue to perform testing; Latitude does not.{br}{br}In this case, Signatera could not be performed, most commonly due to insufficient tissue. However, as Latitude does not require tissue, Latitude was able to be performed. The following results are possible:",
  },
  understandingYourResultsOncologyEmpowerFirstCard: {
    id: "understandingYourResultsOncologyEmpowerFirstCard",
    defaultMessage:
      "<b>Positive:</b> A variant was found that may be associated with an increased risk for certain cancer types. These are called pathogenic or likely pathogenic variants. People with a positive result may have a higher risk for certain cancers than the average person. Talk to your healthcare provider about options for cancer screening and prevention.",
  },
  understandingYourResultsOncologyEmpowerSecondCard: {
    id: "understandingYourResultsOncologyEmpowerSecondCard",
    defaultMessage:
      "<b>Negative:</b> No variant known to increase risk for cancers was found in the genes tested. If your test result is negative, your healthcare provider will consider your personal and family history to estimate your cancer risk and decide whether you should have additional cancer screening beyond what is recommended for the general population.",
  },
  understandingYourResultsOncologyEmpowerThirdCard: {
    id: "understandingYourResultsOncologyEmpowerThirdCard",
    defaultMessage:
      "<b>Variant of Uncertain Significance (VUS):</b> A variant has been identified, but more research is needed to know if it is associated with an increased risk for cancer. VUS status can change over time as more information becomes available. If this happens, you and your healthcare provider will receive an updated report.",
  },
  understandingYourResultsOncologyEmpowerFourthCard: {
    id: "understandingYourResultsOncologyEmpowerFourthCard",
    defaultMessage:
      "<b>Test Not Performed (TNP):</b> A TNP test result means the test was not able to be performed and there is no result. Your report will contain details about the reason the testing was not performed. Examples may include sample stability or the quality of the sample.",
  },
  understandingYourResultsOncologyAlteraFirstCard: {
    id: "understandingYourResultsOncologyAlteraFirstCard",
    defaultMessage:
      "<b>Tumor Genomic Alterations:</b> This includes any genetic variants identified in your tumor that may be associated with FDA-approved cancer therapies and/or potential clinical trials. These variants are called genomic alterations or genomic targets.",
  },
  understandingYourResultsOncologyAlteraSecondCard: {
    id: "understandingYourResultsOncologyAlteraSecondCard",
    defaultMessage:
      "<b>Tumor Genomic Alteration Details:</b> This includes more information about the genomic alterations identified in your tumor and details regarding any associated therapies.",
  },
  understandingYourResultsOncologyAlteraThirdCard: {
    id: "understandingYourResultsOncologyAlteraThirdCard",
    defaultMessage:
      "<b>Clinical Trials:</b> This includes potential clinical trials that are available based on the genomic alterations identified in your tumor. There may be additional trials available or other criteria that determine whether or not you are eligible. All clinical trial considerations should be discussed with your healthcare provider.",
  },
  understandingYourResultsOncologyAlteraFourthCard: {
    id: "understandingYourResultsOncologyAlteraFourthCard",
    defaultMessage:
      "<b>Variant of Uncertain Significance (VUS):</b> This lists all the other variants identified in your tumor, but more research is needed to know the impact of these variants and if there is a cancer therapy that could be beneficial.",
  },
  understandingYourResultsOncologyAlteraFifthCard: {
    id: "understandingYourResultsOncologyAlteraFifthCard",
    defaultMessage:
      "<b>Test Not Performed (TNP):</b> A TNP test result means the test was not able to be performed and there is no result. Your report will contain details about the reason the testing was not performed. Examples may include sample stability, not enough tissue or the quality of the sample.",
  },
  understandingYourResultsOncologySignateraFirstCard: {
    id: "understandingYourResultsOncologySignateraFirstCard",
    defaultMessage:
      "<b>Negative:</b> A negative test result means your tumor’s DNA was not detected in your blood. Negative results may change over time. A negative Signatera result doesn’t guarantee that tumor DNA was not in your blood, nor that it will never be detected in the future.",
  },
  understandingYourResultsOncologySignateraSecondCard: {
    id: "understandingYourResultsOncologySignateraSecondCard",
    defaultMessage:
      "<b>Positive:</b> A positive test result means your tumor’s DNA was detected in your blood. These results should be reviewed with your healthcare provider to discuss options for cancer monitoring or treatment. What this means for you will depend on where you are in your cancer journey.",
  },
  understandingYourResultsOncologySignateraThirdCard: {
    id: "understandingYourResultsOncologySignateraThirdCard",
    defaultMessage:
      "<b>Test Not Performed (TNP):</b> A TNP test result means the test was not able to be performed and there is no result. Your report will contain details about the reason the testing was not performed. Examples may include sample stability, not enough tissue or the quality of the sample.",
  },
  understandingYourResultsOncologyLatitudeFirstCard: {
    id: "understandingYourResultsOncologyLatitudeFirstCard",
    defaultMessage:
      "<b>Positive:</b> A positive test result means that methylation signatures common in the type of cancer you were diagnosed with were found in your sample. These results should be reviewed with your healthcare provider to discuss cancer monitoring or treatment options. What this means for you will depend on where you are in your cancer journey.",
  },
  understandingYourResultsOncologyLatitudeSecondCard: {
    id: "understandingYourResultsOncologyLatitudeSecondCard",
    defaultMessage:
      "<b>Negative:</b> A negative test result means that we did not find methylation signatures common in the type of cancer you were diagnosed with in your sample. Negative results may change over time. A negative Latitude result does not guarantee that there were no signatures from your cancer type in your blood, nor that it will never be detected in the future.",
  },
  understandingYourResultsOncologyLatitudeThirdCard: {
    id: "understandingYourResultsOncologyLatitudeThirdCard",
    defaultMessage:
      "<b>Test Not Performed (TNP):</b> A TNP test result means the test could not be performed, and there is no result. Your report will contain details about the reason the testing was not performed. Examples may include sample stability or sample quality.",
  },
  understandingYourResultsOncologyReflexToLatitudeFirstCard: {
    id: "understandingYourResultsOncologyReflexToLatitudeFirstCard",
    defaultMessage:
      "<b>Positive:</b> A positive test result means that methylation signatures common in the type of cancer you were diagnosed with were found in your sample. These results should be reviewed with your healthcare provider to discuss cancer monitoring or treatment options. What this means for you will depend on where you are in your cancer journey.",
  },
  understandingYourResultsOncologyReflexToLatitudeSecondCard: {
    id: "understandingYourResultsOncologyReflexToLatitudeSecondCard",
    defaultMessage:
      "<b>Negative:</b> A negative test result means that we did not find methylation signatures common in the type of cancer you were diagnosed with in your sample. Negative results may change over time. A negative Latitude result does not guarantee that there were no signatures from your cancer type in your blood, nor that it will never be detected in the future.",
  },
  understandingYourResultsOncologyReflexToLatitudeThirdCard: {
    id: "understandingYourResultsOncologyReflexToLatitudeThirdCard",
    defaultMessage:
      "<b>Test Not Performed (TNP):</b> A TNP test result means the test could not be performed, and there is no result. Your report will contain details about the reason the testing was not performed. Examples may include sample stability or sample quality.",
  },
  understandingYourResultsOncologySignateraFooterParagraph: {
    id: "understandingYourResultsOncologySignateraFooterParagraph",
    defaultMessage:
      "Because results can change over time, Signatera testing is designed to be repeated over the course of your cancer care as directed by your healthcare provider. The overall result as well as the trend in the amount of tumor DNA detected over time can be used by your healthcare provider to make a treatment plan personalized to you.{br}Test results should be interpreted in the context of your medical and cancer history. Discuss your results and your history with your healthcare provider to understand next steps in your cancer treatment journey.",
  },
  understandingYourResultsOncologyLatitudeFooterParagraph: {
    id: "understandingYourResultsOncologyLatitudeFooterParagraph",
    defaultMessage:
      "Because these results can change over time, Latitude is designed to be repeated over the course of your cancer care as directed by your healthcare provider. You and your healthcare provider can use the overall results to make an informed treatment plan personalized for you.{br}{br}Test results should be interpreted within the context of your medical and cancer history. Discuss your results and your history with your healthcare provider to understand next steps in your cancer treatment journey.",
  },
  understandingYourResultsOncologyReflexToLatitudeFooterParagraph: {
    id: "understandingYourResultsOncologyReflexToLatitudeFooterParagraph",
    defaultMessage:
      "Because results can change over time, both Latitude and Signatera are designed to be repeated over the course of your cancer care as directed by your healthcare provider. You and your healthcare provider can use the overall results to make an informed treatment plan personalized for you.{br}{br}Test results should be interpreted within the context of your medical and cancer history. Discuss your results and your history with your healthcare provider to understand next steps in your cancer treatment journey.",
  },
  understandingYourResultsOncologyAlteraFooterParagraph: {
    id: "understandingYourResultsOncologyAlteraFooterParagraph",
    defaultMessage:
      "Test results should be interpreted in the context of your medical and cancer history. Discuss your results and your history with your healthcare provider to understand next steps in your cancer treatment journey.",
  },
  understandingYourResultsOncologyEmpowerFooterParagraph: {
    id: "understandingYourResultsOncologyEmpowerFooterParagraph",
    defaultMessage:
      "Test results should be interpreted in the context of your medical and family history. Discuss your results and your history with your healthcare provider to understand your personal risk of cancer. You may be eligible for more cancer screening and prevention options.",
  },
  understandingYourResultssgNIPTFirstParagraph: {
    id: "understandingYourResultssgNIPTFirstParagraph",
    defaultMessage:
      "The Fetal Focus test is a screening blood test during pregnancy that looks for specific genetic conditions a baby might inherit from their parents. The test results can show if your baby is at high or low risk for a condition that you were found to be a carrier of on your Horizon Carrier Screening. The Fetal Focus test may not include all of the conditions on your Horizon Carrier Screening, so your baby may be at high risk for conditions not included on the Fetal Focus test.{br}The following results are possible on this screening test:",
  },
  understandingYourResultssgNIPTMessagesFirstCard: {
    id: "understandingYourResultssgNIPTMessagesFirstCard",
    defaultMessage:
      "<b>High Risk:</b> This result means there is an increased chance your baby has the genetic condition. Because this is a screening test, this result does not mean that your baby has the condition. To know for sure if the baby has the condition, follow up diagnostic testing, such as an amniocentesis during the pregnancy or a blood test after birth, will be needed.",
  },
  understandingYourResultssgNIPTMessagesSecondCard: {
    id: "understandingYourResultssgNIPTMessagesSecondCard",
    defaultMessage:
      "<b>Low Risk:</b> This result means your baby has a low chance of having the genetic condition.",
  },
  understandingYourResultssgNIPTMessagesThirdCard: {
    id: "understandingYourResultssgNIPTMessagesThirdCard",
    defaultMessage:
      "<b>Inconclusive:</b> This result means the test couldn’t tell if your baby is at high or low risk. In some cases, it may be possible to get a result with a new blood sample.",
  },
  understandingYourResultssgNIPTMessagesFourthCard: {
    id: "understandingYourResultssgNIPTMessagesFourthCard",
    defaultMessage:
      "<b>Test Not Performed (TNP):</b> A TNP test result means the test was not completed and there is no result. Your report may explain why the testing was not performed. For example, you may see a TNP result for this test if you were not a carrier for conditions screened for by Fetal Focus.",
  },
  understandingYourResultsOncologyDpydTitle: {
    id: "understandingYourResultsOncologyDpydTitle",
    defaultMessage: "DPYD - Genotyping",
  },
  understandingYourResultsDpydImportantNote: {
    id: "understandingYourResultsDpydImportantNote",
    defaultMessage:
      "<b>Important note:</b> The results you are about to view are for your DPYD test, not for your Signatera results. Signatera typically takes longer to analyze. You will get a notification from Natera when your Signatera results are ready for you to view.",
  },
  understandingYourResultsDpydBodyParagraph1: {
    id: "understandingYourResultsDpydBodyParagraph1",
    defaultMessage:
      "DPYD testing checks for inherited changes in the DPYD gene, which provides instructions for the DPD enzyme that helps the body break down certain chemotherapy drugs like 5-FU and capecitabine. Because this test looks at your germline DNA (not tumor DNA), it can show whether your body may have trouble processing these medicines. If someone has a DPYD variant, the drug can build up and cause serious side effects. Knowing this before treatment helps clinicians adjust dosing or choose another therapy to make treatment safer.",
  },
  understandingYourResultsDpydBodyParagraph2: {
    id: "understandingYourResultsDpydBodyParagraph2",
    defaultMessage:
      "Your report will include an activity score and a metabolizer category, and your metabolizer category is based on your activity score. The different categories for metabolizers are:",
  },
  understandingYourResultsDpydMetabolizerNormal: {
    id: "understandingYourResultsDpydMetabolizerNormal",
    defaultMessage:
      "<b>Normal Metabolizer:</b> Your body is expected to break down fluoropyrimidine chemotherapy at the usual rate. Standard starting doses are typically appropriate. Your doctor will still monitor you for side effects, as they would for any patient.",
  },
  understandingYourResultsDpydMetabolizerIntermediate: {
    id: "understandingYourResultsDpydMetabolizerIntermediate",
    defaultMessage:
      "<b>Intermediate Metabolizer:</b> Your body breaks down these chemotherapy medicines more slowly than usual. You may have a higher risk of serious side effects and your doctor will usually start with a lower dose. Your dose may be adjusted depending on how you tolerate treatment.",
  },
  understandingYourResultsDpydMetabolizerPoor: {
    id: "understandingYourResultsDpydMetabolizerPoor",
    defaultMessage:
      "<b>Poor Metabolizer:</b> Your body has very little or no ability to break down these chemotherapy medicines. You are at high risk for severe or life-threatening side effects. Your doctor may recommend a much lower dose, or recommend a different medication altogether.",
  },
  understandingYourResultsDpydFooter: {
    id: "understandingYourResultsDpydFooter",
    defaultMessage:
      "Your test results should always be looked at alongside your full medical and cancer history. Discuss your results and your history with your healthcare provider to understand next steps in your cancer treatment journey.",
  },
});

type understandingYourResultsOncologyOncologyContentProps = {
  testType: TestType;
  isReflexed?: boolean;
};

export const UnderstandingYourResultsOncologyContent: FC<
  understandingYourResultsOncologyOncologyContentProps
> = ({ testType, isReflexed }) => {
  const intl = useIntl();

  const getHeadlineByTestType = () => {
    switch (testType) {
      case TestType.ALTERA:
        return intl.formatMessage(
          messages.understandingYourResultsOncologyAlteraHeadline
        );
      case TestType.EMPOWER:
        return intl.formatMessage(
          messages.understandingYourResultsOncologyEmpowerHeadline
        );
      case TestType.SINGLE_GENE_NIPT:
        return intl.formatMessage(
          messages.understandingYourResultsReadysgNIPTHeadline
        );
      case TestType.LATITUDE:
        return intl.formatMessage(
          messages.understandingYourResultsReadyLatitudeHeadline
        );
      case TestType.DPYD:
        return intl.formatMessage(
          messages.understandingYourResultsOncologyDpydTitle
        );
      default:
        return intl.formatMessage(
          messages.understandingYourResultsOncologySignateraHeadline
        );
    }
  };

  /** Combo-order warning (DPYD only) — shown above the first paragraph */
  const getImportantNoteByTestType = (): ReactNode | undefined => {
    if (testType === TestType.DPYD) {
      return intl.formatMessage(
        messages.understandingYourResultsDpydImportantNote,
        {
          b: (chunks) => <b>{chunks}</b>,
        }
      );
    }
    return undefined;
  };

  const getFirstParagraphByTestType = () => {
    switch (testType) {
      case TestType.ALTERA:
        return intl.formatMessage(
          messages.understandingYourResultsOncologyAlteraFirstParagraph,
          { br: <br className="br" /> }
        );
      case TestType.EMPOWER:
        return intl.formatMessage(
          messages.understandingYourResultsOncologyEmpowerFirstParagraph
        );
      case TestType.SINGLE_GENE_NIPT:
        return intl.formatMessage(
          messages.understandingYourResultssgNIPTFirstParagraph,
          { br: <br className="br" /> }
        );
      case TestType.LATITUDE:
        if (isReflexed) {
          return intl.formatMessage(
            messages.understandingYourResultsOncologyReflexToLatitudeFirstParagraph,
            { br: <br className="br" /> }
          );
        } else {
          return intl.formatMessage(
            messages.understandingYourResultsOncologyLatitudeFirstParagraph
          );
        }
      case TestType.DPYD:
        return intl.formatMessage(
          messages.understandingYourResultsDpydBodyParagraph1
        );
      default:
        return intl.formatMessage(
          messages.understandingYourResultsOncologySignateraFirstParagraph
        );
    }
  };

  /** Intro line before metabolizer categories (DPYD only) */
  const getSecondParagraphByTestType = (): string | undefined => {
    switch (testType) {
      case TestType.DPYD:
        return intl.formatMessage(
          messages.understandingYourResultsDpydBodyParagraph2
        );
      default:
        return undefined;
    }
  };

  const getTestCardDescriptionByTestType = () => {
    switch (testType) {
      case TestType.ALTERA:
        return getAlteraDescriptionTestCard();
      case TestType.EMPOWER:
        return getEmpowerDescriptionTestCard();
      case TestType.SINGLE_GENE_NIPT:
        return getsgNIPTDescriptionTestCard();
      case TestType.LATITUDE:
        if (isReflexed) {
          return getReflexToLatitudeDescriptionTestCard();
        } else {
          return getLatitudeDescriptionTestCard();
        }
      case TestType.DPYD:
        return getDpydDescriptionTestCard();
      default:
        return getSignateraDescriptionTestCard();
    }
  };

  const getFooterParagraphByTestType = () => {
    switch (testType) {
      case TestType.ALTERA:
        return intl.formatMessage(
          messages.understandingYourResultsOncologyAlteraFooterParagraph
        );
      case TestType.EMPOWER:
        return intl.formatMessage(
          messages.understandingYourResultsOncologyEmpowerFooterParagraph
        );
      case TestType.SIGNATERA:
        return intl.formatMessage(
          messages.understandingYourResultsOncologySignateraFooterParagraph,
          { br: <br className="br" /> }
        );
      case TestType.LATITUDE:
        if (isReflexed) {
          return intl.formatMessage(
            messages.understandingYourResultsOncologyReflexToLatitudeFooterParagraph,
            { br: <br className="br" /> }
          );
        } else {
          return intl.formatMessage(
            messages.understandingYourResultsOncologyLatitudeFooterParagraph,
            { br: <br className="br" /> }
          );
        }
      case TestType.DPYD:
        return intl.formatMessage(messages.understandingYourResultsDpydFooter);
      default:
        return undefined;
    }
  };

  const getAlteraDescriptionTestCard = () => {
    const alteraMessages = [
      messages.understandingYourResultsOncologyAlteraFirstCard,
      messages.understandingYourResultsOncologyAlteraSecondCard,
      messages.understandingYourResultsOncologyAlteraThirdCard,
      messages.understandingYourResultsOncologyAlteraFourthCard,
      messages.understandingYourResultsOncologyAlteraFifthCard,
    ];
    return generateTestCardDescription(alteraMessages);
  };

  const getDpydDescriptionTestCard = () => {
    const dpydMetabolizerMessages = [
      messages.understandingYourResultsDpydMetabolizerNormal,
      messages.understandingYourResultsDpydMetabolizerIntermediate,
      messages.understandingYourResultsDpydMetabolizerPoor,
    ];
    return generateTestCardDescription(dpydMetabolizerMessages);
  };

  const getEmpowerDescriptionTestCard = () => {
    const empowerMessages = [
      messages.understandingYourResultsOncologyEmpowerFirstCard,
      messages.understandingYourResultsOncologyEmpowerSecondCard,
      messages.understandingYourResultsOncologyEmpowerThirdCard,
      messages.understandingYourResultsOncologyEmpowerFourthCard,
    ];
    return generateTestCardDescription(empowerMessages);
  };

  const getSignateraDescriptionTestCard = () => {
    const signateraMessages = [
      messages.understandingYourResultsOncologySignateraFirstCard,
      messages.understandingYourResultsOncologySignateraSecondCard,
      messages.understandingYourResultsOncologySignateraThirdCard,
    ];
    return generateTestCardDescription(signateraMessages);
  };

  const getsgNIPTDescriptionTestCard = () => {
    const cardMessages = [
      messages.understandingYourResultssgNIPTMessagesFirstCard,
      messages.understandingYourResultssgNIPTMessagesSecondCard,
      messages.understandingYourResultssgNIPTMessagesThirdCard,
      messages.understandingYourResultssgNIPTMessagesFourthCard,
    ];
    return generateTestCardDescription(cardMessages);
  };

  const getLatitudeDescriptionTestCard = () => {
    const latitudeMessages = [
      messages.understandingYourResultsOncologyLatitudeFirstCard,
      messages.understandingYourResultsOncologyLatitudeSecondCard,
      messages.understandingYourResultsOncologyLatitudeThirdCard,
    ];
    return generateTestCardDescription(latitudeMessages);
  };
  const getReflexToLatitudeDescriptionTestCard = () => {
    const reflextoLatitudeMessages = [
      messages.understandingYourResultsOncologyReflexToLatitudeFirstCard,
      messages.understandingYourResultsOncologyReflexToLatitudeSecondCard,
      messages.understandingYourResultsOncologyReflexToLatitudeThirdCard,
    ];
    return generateTestCardDescription(reflextoLatitudeMessages);
  };

  const generateTestCardDescription = (
    messages: {
      id: string;
      defaultMessage: string;
    }[]
  ) => {
    return (
      <>
        {messages.map((message, index) => (
          <div key={index}>
            {intl.formatMessage(message, {
              b: (chunks) => <b>{chunks}</b>,
              br: <br className="br" />,
            })}
          </div>
        ))}
      </>
    );
  };
  const testResultsTexts = useTestResultsTexts();

  const descriptionByTestType =
    testType &&
    testResultsTexts.understandingResultsHeadline(
      testType,
      BusinessUnits.ONCOLOGY
    );

  const importantNote = getImportantNoteByTestType();
  const secondParagraph = getSecondParagraphByTestType();
  const footerParagraph = getFooterParagraphByTestType();
  const isDpyd = testType === TestType.DPYD;

  return (
    <div
      className={
        isDpyd
          ? "understanding-your-results__content-oncology understanding-your-results__content-oncology--dpyd"
          : "understanding-your-results__content-oncology"
      }
    >
      <Svg content={DocumentsMagnifyingGlass} />
      <h2>{getHeadlineByTestType()}</h2>
      <h4>{descriptionByTestType}</h4>
      <div className="understanding-your-results__content-oncology-card-wrapper">
        {importantNote && (
          <div
            className="understanding-your-results__dpyd-important-note"
            role="note"
          >
            <p>{importantNote}</p>
          </div>
        )}
        <p>{getFirstParagraphByTestType()}</p>
        {secondParagraph && <p>{secondParagraph}</p>}
        {isDpyd ? (
          <div className="understanding-your-results__dpyd-metabolizers">
            {getTestCardDescriptionByTestType()}
          </div>
        ) : (
          <div className="understanding-your-results__content-oncology-card-description">
            {getTestCardDescriptionByTestType()}
          </div>
        )}
      </div>
      {footerParagraph && (
        <div className="understanding-your-results__content-oncology-footer">
          {footerParagraph}
        </div>
      )}
    </div>
  );
};
